import React, { useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../../utils/api';

const API_URL = getApiUrl();

// LMS V52 — the trial class is booked in the LMS the moment Date + Batch + Slot are saved.
// This strip is the receipt: what the LMS did, and what SoulAI's check-in suggests (guidance
// only — whatever the associate books is what the learner sees).
export const TrialLmsStatus = ({ lead, onRefresh, selectedBatchName }) => {
  const [busy, setBusy] = useState(false);
  const bk = lead?.lms_trial_booking;
  const pref = lead?.lms_trial_preference;
  const risk = lead?.trial_risk_level;
  // LMS V56 O6 — batches follow the resolved country, never the phone number. An international
  // learner in an India batch (or the reverse) is the "India lead / US batch" contradiction.
  const intl = (lead?.domestic_or_international || '') === 'international';
  const batch = (selectedBatchName || bk?.batch_name || '').toUpperCase();
  const usBatch = batch.includes('US');
  const countryBatchClash = batch && ((intl && !usBatch) || (!intl && usBatch && lead?.country));
  if (!bk && !pref && !risk && !countryBatchClash) return null;

  const retry = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/leads/${lead.lead_id}/trial-booking/sync`, {},
        { withCredentials: true });
      if (res.data?.ok) toast.success('Booked in the LMS — it shows on her dashboard');
      else toast.error(res.data?.message || 'The LMS still refused the booking');
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reach the LMS');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="md:col-span-2 space-y-2" data-testid="trial-lms-status">
      {countryBatchClash && (
        <div className="flex items-start gap-2 rounded-lg px-3 py-2 text-sm bg-amber-50 text-amber-900 border border-amber-200" data-testid="country-batch-clash">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {intl
              ? `She resolves to ${lead?.country || 'outside India'} — international learners belong in the MWF US batch, not ${selectedBatchName || bk?.batch_name}.`
              : `${lead?.country} learner in the US batch — confirm her location, or mark her International if that is right.`}
          </span>
        </div>
      )}
      {risk && (
        <div
          className={`flex items-start gap-2 rounded-lg px-3 py-2 text-sm ${risk === 'high'
            ? 'bg-rose-50 text-rose-800 border border-rose-200'
            : 'bg-emerald-50 text-emerald-800 border border-emerald-200'}`}
          data-testid={`trial-risk-${risk}`}
        >
          <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <span className="font-medium capitalize">{risk} risk</span>
            {lead.trial_weeks_pregnant ? ` · ${lead.trial_weeks_pregnant} weeks` : ''}
            {lead.trial_risk_reasons?.length ? ` · ${lead.trial_risk_reasons.join(', ').replace(/_/g, ' ')}` : ''}
            {lead.trial_recommended_batches?.length && (
              <span className="block text-xs mt-0.5">
                SoulAI suggests <strong>{lead.trial_recommended_batches.join(' / ')}</strong> — guidance only,
                your choice above is what she will see.
              </span>
            )}
          </div>
        </div>
      )}

      {pref && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600"
             data-testid="trial-preference">
          She tapped a preferred slot in the app: <strong>{pref.preferred_date || '—'} {pref.preferred_time || ''} {pref.batch_name || ''}</strong>. Nothing is booked until you save the three fields above.
        </div>
      )}

      {bk && (
        <div
          className={`flex items-start justify-between gap-3 rounded-lg px-3 py-2 text-sm ${bk.ok
            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
            : 'bg-amber-50 text-amber-900 border border-amber-200'}`}
          data-testid={bk.ok ? 'trial-lms-booked' : 'trial-lms-failed'}
        >
          <div className="flex items-start gap-2">
            {bk.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                   : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
            <div>
              {bk.ok ? (
                <>
                  <span className="font-medium">Booked in the LMS</span>
                  <span className="block text-xs mt-0.5">
                    {bk.batch_name} · {bk.scheduled_date}{bk.scheduled_time ? ` ${bk.scheduled_time}` : ''} — on her dashboard, attendance tagged Trial.
                  </span>
                  {bk.warning?.message && (
                    <span className="block text-xs mt-1 text-amber-800" data-testid="trial-risk-mismatch">
                      {bk.warning.message}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span className="font-medium">Not booked in the LMS yet</span>
                  <span className="block text-xs mt-0.5">{bk.message}</span>
                </>
              )}
            </div>
          </div>
          {!bk.ok && (
            <button
              type="button"
              onClick={retry}
              disabled={busy}
              className="shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-60"
              data-testid="trial-lms-retry"
            >
              <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} /> Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
};
